import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface ServiceAccount {
    Id: string;
    Username: string | null;
    ClientId: string | null;
    Tenants: string[];
    PermissionIdsByTenant: Record<string, string>;
    IsActive: boolean;
    Deleted?: boolean;
}

export interface ServiceAccountCredentials {
    ClientId: string;
    ClientSecret: string;
    TokenEndpoint: string;
}

export interface CreateServiceAccountRequest {
    Username: string;
    Tenants: string[];
    PermissionIdsByTenant: Record<string, string>;
    IsActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ServiceAccountService {
  private api: AxiosInstance;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private authService: KeycloakAuthService,
    private apiConfig: ApiConfigService
  ) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.api.interceptors.request.use((config) => {
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = tenantId
          ? `${baseUrl}/${tenantId}/service-account`
          : `${baseUrl}/service-account`;
      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async getServiceAccount(id: string): Promise<ServiceAccount | null> {
    try {
      const response = await this.api.get(`/${id}`);
      return response.data;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  async getAllServiceAccounts(): Promise<ServiceAccount[] | null> {
    try {
      const response = await this.api.get('/');
      return response.data;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  async createServiceAccount(request: CreateServiceAccountRequest): Promise<ServiceAccountCredentials> {
    try {
      // ACIT-534: send a pre-serialized JSON string. axios deep-merges object bodies via mergeConfig and silently drops
      // keys named 'prototype'/'constructor'/'__proto__' (e.g. a tenant called 'prototype' in PermissionIdsByTenant).
      const response = await this.api.post('/', JSON.stringify(request));
      return response.data;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  async updateServiceAccount(serviceAccount: ServiceAccount): Promise<AxiosResponse | null> {
    try {
      // ACIT-534: send a pre-serialized JSON string. axios deep-merges object bodies via mergeConfig and silently drops
      // keys named 'prototype'/'constructor'/'__proto__' (e.g. a tenant called 'prototype' in PermissionIdsByTenant).
      const response = await this.api.put(`/${serviceAccount.Id}`, JSON.stringify(serviceAccount));
      return response;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  async deleteServiceAccount(id: string): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.delete(`/${id}`);
      return response;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  async restoreServiceAccount(id: string): Promise<AxiosResponse | null> {
    try {
      // Assumes restore is exposed through PATCH until a dedicated restore endpoint is available.
      const response = await this.api.patch(`/${id}`, { Deleted: false });
      return response;
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }
}
